<template>
    <div ref="appHeaderRef" class="stf-navbar">
        <StfContainer full>
            <StfContainer>
                <nav class="stf-navbar__container">
                    <img
                        :src="domain.logo"
                        alt="tm Logo"
                        class="stf-navbar-logo"
                    />
                    <ul class="stf-navbar__menu--desktop">
                        <StfNavItem
                            v-for="item in navItems"
                            :key="item.text"
                            :text="item.text"
                            :href="item.href"
                        />
                    </ul>
                    <div class="stf-navbar__menu--mobile" @click="toggleMobileMenu">
                        <StfIcon :icon="mobileMenuOpen ? 'xMark' : 'bars'" color="white" />
                    </div>
                </nav>
            </StfContainer>
        </StfContainer>
    </div>
    <!-- Full screen mobile menu -->
    <Transition name="mobile-menu">
        <div v-if="mobileMenuOpen" class="stf-navbar__fullscreen-menu">
            <div class="stf-navbar__fullscreen-menu-content">
                <ul class="stf-navbar__mobile-links">
                    <li v-for="item in navItems" :key="item.text" @click="closeMobileMenu">
                        <a :href="item.href">{{ item.text }}</a>
                    </li>
                </ul>
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import StfNavItem from './StfNavItem.vue';
import useAppHeader from '@/composables/useAppHeader';
import useDomain from '@/composables/domainDependable';

interface NavItem {
    text: string;
    href: string;
}

defineProps<{
    navItems: NavItem[];
}>();

const domain = useDomain();
const mobileMenuOpen = ref(false);
const { setHeaderHeight } = useAppHeader();

const appHeaderRef = ref<HTMLElement | null>(null);

onMounted(() => {
    if (appHeaderRef.value) {
        setHeaderHeight(appHeaderRef.value.clientHeight);
    }
})

const toggleMobileMenu = () => {
    mobileMenuOpen.value = !mobileMenuOpen.value;

    // Prevent scrolling when menu is open
    if (mobileMenuOpen.value) {
        document.body.style.overflow = 'hidden';
    } else {
        document.body.style.overflow = '';
    }
};

const closeMobileMenu = () => {
    mobileMenuOpen.value = false;
    document.body.style.overflow = '';
};
</script>

<style lang="scss">
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

.stf-navbar {
  width: 100%;
  height: 72px;
  background-color: vars.$color-brand;
  display: flex;
  justify-content: space-around;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 102; // Ensure it's above mobile menu (101)

  @include mixins.mobile {
    height: 65px;
  }
}

.stf-navbar__container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
}

.stf-navbar-logo {
  height: 36px;
}

.stf-navbar__menu--desktop {
  display: flex;
  align-items: center;
  gap: 48px;
  list-style-type: none;
  padding: 0;
  margin: 0;

  @include mixins.mobile {
    display: none;
  }
}

.stf-navbar__menu--mobile {
  display: none;
  cursor: pointer;

  @include mixins.mobile {
    display: block;
  }

  i {
    font-size: vars.$font-size-24;
    color: vars.$color-white;
  }
}

.stf-navbar__fullscreen-menu {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: vars.$color-brand;
  z-index: 101; // Ensure it's above paypal button (100)
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.stf-navbar__fullscreen-menu-content {
  width: 100%;
  max-width: 400px;
  padding: 0 20px;
}

.stf-navbar__mobile-links {
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    margin: 20px 0;
    text-align: center;

    a {
      color: vars.$color-white;
      font-size: vars.$font-size-24;
      text-decoration: none;
      font-weight: vars.$font-weight-medium;
      transition: opacity 0.3s ease;

      &:hover {
        opacity: 0.8;
      }
    }
  }
}

// Transition animations for mobile menu
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: opacity 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
}
</style>
